import { createSelector } from 'reselect'
import * as R from 'ramda'
import { getResources } from 'store/reducers/resources'
import { LABEL_TRIPTYPE_FLIGHT_WITH_HOTEL_ID, LABEL_TRIPTYPE_HOTEL_ID, LABEL_TRIPTYPE_ID } from 'constants/labels'
import { removeInvalidPackageElements } from 'domain/packageElements'
import {
  PackageResource,
  Resources
} from 'types/typescript'
import { getIsHideReferencePriceFeatureEnabled, getShouldDoubleThePriceFeatureEnabled } from './application'
import { getFiltersProducts } from './filters'
import {
  GetActivePackage,
  GetActivePackageContent,
  GetActivePackageKey,
  GetActivePackagePriceInfos,
  GetActivePackageShouldDoubleThePrice,
  GetActivePackageTitle,
  GetCurrentPackages,
  GetCurrentPackagesKeys,
  GetCurrentSelection,
  GetFirstAvailablePackage,
  GetHasActivePackage,
  GetHasActivePackageOnlyOnePrice,
  GetHasOnlyOnePackage,
  GetHasPickersFetchedError,
  GetIsActivePackageFetched,
  GetIsCheapestPriceToggled,
  GetPackageElements,
  GetPickersBindings,
  GetPickersBindingsStock,
  GetPriceInfosFromActivePackageInFilteredProducts,
  GetPricerState,
  GetPriceUnit
} from './package.types'
import {
  getCurrentProduct,
  getCurrentProductKey
} from './product'

export const getPricerState: GetPricerState = R.prop('pricer')

export const getCurrentPackagesKeys: GetCurrentPackagesKeys = createSelector(
  getCurrentProduct,
  R.ifElse(
    R.isNil,
    R.always([]),
    R.compose(
      R.defaultTo([]),
      R.prop('resources')
    )
  )
)

export const getCurrentPackages: GetCurrentPackages = createSelector(
  getResources,
  getCurrentPackagesKeys,
  getIsHideReferencePriceFeatureEnabled,
  (allResources: Resources, packagesKeys: Array<keyof Resources>, shouldHideReferencePrice: boolean) => (
    packagesKeys.map(key => {
      const resource = allResources[key] as PackageResource

      if (!shouldHideReferencePrice || !resource?.ecomm?.price) return resource

      return { ...resource, ecomm: { ...resource.ecomm, price: { ...resource.ecomm?.price, msrp: null } } }
    })
  )
)

export const getCurrentPackagesTripTypesLabels = createSelector(
  getCurrentPackages,
  (packagesRessource: PackageResource[]) => {
    const allTripTypesLabels = packagesRessource
      ?.map(pack => pack?.ecomm?.labels?.filter(({ id }) => id === LABEL_TRIPTYPE_ID)) // Retrieve all triptypes from all packages of the current product
      ?.map(triptypesLabels => triptypesLabels?.map(label => label.values[0]?.id)) // Extract the ID from those triptypes labels
      ?.flatMap(labelId => labelId) ?? [] // Flatten nested arrays into a single array
    const uniqTripTypesLabels = [...new Set(allTripTypesLabels)] // Remove duplicates

    return uniqTripTypesLabels
  }
)

export const getHasCurrentPackagesToDisclaimUnits = createSelector(
  getCurrentPackagesTripTypesLabels,
  (currentPackagesTripTypesLabels: number[]) => [LABEL_TRIPTYPE_FLIGHT_WITH_HOTEL_ID, LABEL_TRIPTYPE_HOTEL_ID].every(labelID => currentPackagesTripTypesLabels.includes(labelID))
)

export const getHasOnlyOnePackage: GetHasOnlyOnePackage = createSelector(
  getCurrentPackages,
  currentPackages => currentPackages.length === 1
)

export const getActivePackageKey: GetActivePackageKey = createSelector(
  getPricerState,
  R.prop('activePackageKey')
)

export const getActivePackage: GetActivePackage = createSelector(
  getActivePackageKey,
  getResources,
  getIsHideReferencePriceFeatureEnabled,
  (key, resources, shouldHideReferencePrice) => {
    const resource = R.prop(key)(resources)

    if (!shouldHideReferencePrice || !resource?.ecomm?.price) return resource

    return { ...resource, ecomm: { ...resource.ecomm, price: { ...resource.ecomm?.price, msrp: null } } }
  }
)

export const getIsActivePackageFetched: GetIsActivePackageFetched = createSelector(
  getActivePackage,
  R.pipe(
    R.defaultTo({}),
    R.prop('fetched')
  )
)

export const getHasActivePackage: GetHasActivePackage = createSelector(
  getActivePackage,
  R.pipe(
    R.defaultTo({}),
    R.prop('currentSelection'),
    R.complement(R.isNil)
  )
)

export const getActivePackageContent: GetActivePackageContent = createSelector(
  getActivePackage,
  R.prop('content')
)

export const getActivePackageTitle: GetActivePackageTitle = createSelector(
  getActivePackageContent,
  R.prop('title')
)

export const getFirstAvailablePackage: GetFirstAvailablePackage = createSelector(
  getCurrentPackages,
  getCurrentProductKey,
  getFiltersProducts,
  (packages, currentProductKey, filteredProduct) => {
    const currentProduct = filteredProduct[currentProductKey]

    if (currentProduct !== undefined) {
      const pack = packages.find(pack => pack.key === currentProduct.packageKey)
      if (pack !== undefined) {
        return pack
      }
    }
    return packages.find(pack => !pack.outOfStock)
  }
)

export const getPriceUnit: GetPriceUnit = createSelector(
  getActivePackage,
  R.path(['ecomm', 'price', 'unit'])
)

export const getPackageElements: GetPackageElements = createSelector(
  getActivePackage,
  R.compose(
    removeInvalidPackageElements,
    R.path(['ecomm', 'packageElements'])
  )
)

export const getCurrentSelection: GetCurrentSelection = createSelector(
  getActivePackage,
  R.path(['currentSelection'])
)

export const getHasPickersFetchedError: GetHasPickersFetchedError = createSelector(
  getActivePackage,
  activePackage => activePackage?.hasPickersFetchedError
)

export const getPickersBindings: GetPickersBindings = createSelector(
  getActivePackage,
  R.path(['ecomm', 'aggregatedPickerBindings'])
)

export const getPickersBindingsStock: GetPickersBindingsStock = createSelector(
  getActivePackage,
  R.path(['ecomm', 'pickersBindingsStock'])
)

export const getActivePackagePriceInfos: GetActivePackagePriceInfos = createSelector(
  getActivePackage,
  R.path(['ecomm', 'priceInfos'])
)

export const getIsCheapestPriceToggled: GetIsCheapestPriceToggled = createSelector(
  getActivePackage,
  R.path(['ecomm', 'isCheapestPriceToggled'])
)

export const getHasActivePackageOnlyOnePrice: GetHasActivePackageOnlyOnePrice = createSelector(
  getActivePackage,
  R.path(['ecomm', 'hasOnlyOnePrice'])
)

export const getActivePackageShouldDoubleThePrice: GetActivePackageShouldDoubleThePrice = createSelector(
  getShouldDoubleThePriceFeatureEnabled,
  getActivePackage,
  (isShouldDoubleThePriceFeatureEnabled, activePackage) => (
    isShouldDoubleThePriceFeatureEnabled &&
    Boolean(activePackage?.ecomm?.shouldDoubleThePrice)
  )
)

export const getPriceInfosFromActivePackageInFilteredProducts: GetPriceInfosFromActivePackageInFilteredProducts = createSelector(
  getFiltersProducts,
  getCurrentProductKey,
  getActivePackageKey,
  (filteredProducts, currentProductKey, activePackageKey) => {
    const packages = filteredProducts[currentProductKey]?.packages

    return packages?.find(({ packageKey }) => packageKey === activePackageKey)
  }
)
